/**
 * FilterPill — one toggle in the case filter bar.
 *
 * WHY EVERY PILL IS ALWAYS RENDERED (v1.141.0). Twelve of the fourteen pills used to
 * render only when they had something to hide (`someCount > 0`), which meant the bar's
 * SHAPE changed as an investigation progressed: `preCompromiseCount` is zero until the
 * analyst classifies a malicious IP, so that pill materialised partway through a case,
 * and post-window behaved the same way. An analyst cannot build habits around controls
 * that appear and disappear, and cannot tell *"this filter is off"* from *"this filter
 * does not exist here"*. It also made the Executive Report's canonical filter profile
 * unsatisfiable — you cannot switch on a pill that is not rendered.
 *
 * So inertness is now SHOWN, not hidden: a zero count still renders, and the pill dims.
 * The filters themselves never depended on the pill being visible — `filteredResults`
 * applies each toggle's state regardless — so this changes reachability and legibility,
 * not behaviour.
 *
 * `disabled` is for a pill that cannot do anything AT ALL, as opposed to one that has
 * nothing to act on right now: post-window with an indeterminate attacker window has no
 * meaning, so it renders visibly disabled with the reason in its tooltip rather than as
 * a live control that silently no-ops (memory feedback_ui_open_api_gated_silent_button).
 *
 * The component stays dumb about semantics — the caller composes the label and decides
 * `engaged` / `inert` / `disabled` — so a three-state toggle like Dismissed uses the same
 * pill as the booleans instead of a second near-copy.
 */

const TONES = {
  blue:   'bg-blue-900/40 border-blue-700/50 text-blue-300',
  purple: 'bg-purple-900/40 border-purple-700/50 text-purple-300',
  orange: 'bg-orange-900/40 border-orange-700/50 text-orange-300',
};

const IDLE = 'bg-gray-800 border-gray-700 text-gray-400 hover:text-gray-300';

export default function FilterPill({
  icon: Icon,
  label,
  engaged = false,
  tone = 'blue',
  inert = false,
  disabled = false,
  title,
  onClick,
}) {
  const base = 'flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-lg border transition-colors whitespace-nowrap shrink-0';
  const state = disabled
    ? 'bg-gray-800 border-gray-700 text-gray-500 cursor-not-allowed opacity-40'
    : `${engaged ? (TONES[tone] || TONES.blue) : IDLE}${inert ? ' opacity-50' : ''}`;

  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={`${base} ${state}`}
      title={title}
    >
      {Icon && <Icon className="h-3.5 w-3.5" />}
      {label}
    </button>
  );
}
